// Полоса действий над сеткой: четыре нажатия, за которые закрывается месяц.
//
// ## Одна кнопка за раз и всегда на одном месте
//
// Состояние ведомости однозначно определяет, что человек может сделать дальше, поэтому кнопка
// ровно одна. Показывать все четыре и гасить неподходящие значило бы предлагать выбор там, где
// его нет, а серая кнопка вдобавок не доводит нажатие до обработчика — человек жмёт в пустоту и
// не понимает, почему ничего не происходит.
//
// Место в ШАПКЕ, а не под сеткой: в ведомости 281 строка, и кнопка под ними означала бы
// прокрутку перед каждым из четырёх нажатий.
//
// ## Отметка выплаты — тоже одна кнопка
//
// До 19.10.2026 у утверждённой ведомости рядом стояли две: «Отметить выплату: сумма» и
// «Отметить выплаченной всю ведомость». Первая ставит отметку всем, у кого её нет, и переводит
// ведомость в «выплачена»; вторая ставила те же отметки, а состояние не трогала. Чем они
// различаются, человеку не говорилось нигде, и сверка подписей 19.10.2026 записала прямо:
// заказчик их перепутает. Теперь отметка, как и остальные шаги, зависит от состояния:
//
//   утверждена                      → «Отметить выплату: сумма»;
//   выплачена, но отмечены не все   → «Отметить выплаченными остальных (N)»: те же отметки
//                                      пачкой, состояние и так уже «выплачена»;
//   выплачена, отмечены все         → ничего: цепочка пройдена.
//
// Выплаченная ведомость без отметок бывает по двум причинам: до 13.10.2026 «Отметить выплату»
// меняла только состояние, а отметку человека можно сторнировать и после выплаты. N считается
// по той же сетке и тем же признаком, что у панели человека (`безОтметкиВыплаты`), и сходится с
// тем, скольким людям сервер поставит отметку.
//
// День перевода спрашивается у обеих и уходит в отметки: деньги уходят и раньше, чем их
// отмечают. Кому перевели не всё, отметку ставят в панели человека ДО общей кнопки: общая уже
// отмеченных не трогает. Поэтому и сумма на кнопке утверждённой — то, что правда запишется: пока
// не отмечен никто, это весь итог ведомости, а когда часть людей уже отмечена построчно — итог
// остальных (`суммаБезОтметкиВыплаты`, тот же признак, что у N). До 19.10.2026 на кнопке всегда
// стоял весь итог, и при двух отмеченных из сорока она обещала перевести больше, чем запишет.
//
// ## Подтверждающих окон в цепочке нет
//
// Подтверждающая поверхность — это постоянно видимые итоги и полоса проверки: человек уже
// смотрит на «42 человека, 1 284 300 ₽». Модалка показала бы те же числа второй раз и удвоила
// бы число нажатий. Единственная модалка — возврат в работу: там нужно основание, и оно
// обязано быть написано словами.
//
// ## Пересчёт в цепочку не входит
//
// Отправка и утверждение пересчитывают сами. Утверждать непересчитанное всё равно нельзя,
// значит это не отдельное решение человека, а часть его действия.
//
// ## Незаписанные правки останавливают цепочку
//
// Пока очередь правок не пуста, утверждать нечего: на сервере лежат ещё не те числа. Полоса
// говорит об этом словами и не гасит кнопку — иначе человек решит, что экран сломался.

import { useState } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';
import { useIsMutating } from '@tanstack/react-query';

import type { PayrollEmployeeOut, PayrollOut } from '@/shared/api/endpoints/payroll';
import { exportPayroll } from '@/shared/api/endpoints/payroll';
import { useAuthStore } from '@/shared/auth/store';
import { ExportButton } from '@/shared/reports/ExportButton';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { can } from '@/shared/rbac/permissions';
import * as Button from '@/shared/ui/button';
import { DateField, todayIso } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import { TextField } from '@/shared/ui/text-field';

import { useMarkPaidBulk } from '../queries';

import {
  useApprovePayroll,
  useMarkPayrollPaid,
  useReturnPayrollToWork,
  useSecondApprovePayroll,
  useSubmitPayroll,
  payrollKeys,
} from './queries';

const ПРАВО_ПОДГОТОВИТЬ = 'core.payroll.submit';
const ПРАВО_СОГЛАСОВАТЬ = 'core.payroll.second_approve';
const ПРАВО_УТВЕРДИТЬ = 'core.payroll.approve';
const ПРАВО_ВЫПЛАТА = 'core.payroll.mark_paid';
const ПРАВО_ВЕРНУТЬ = 'core.payroll.reverse_approval';
/** Отметка факта выплаты — своё право, отдельное от смены состояния ведомости. */
const ПРАВО_ОТМЕТИТЬ = 'core.payout.mark_fact';
const ЛЮДИ = ['человек', 'человека', 'человек'] as const;

/** Минимальная длина основания. Столько же требует сервер: два правила не разойдутся. */
const ОСНОВАНИЕ_МИНИМУМ = 10;

/**
 * Что делать, если кому-то перевели не всё. Одна фраза на обе кнопки отметки: два пересказа
 * одного порядка действий однажды разошлись бы.
 */
const СНАЧАЛА_ЧАСТИЧНЫЕ =
  'Кому перевели не всё, сначала отметьте его выплату в панели человека (нажмите на фамилию, блок «Выплата»), потом нажмите эту кнопку.';

interface Шаг {
  readonly подпись: string;
  readonly пояснение: string;
  readonly право: string;
  readonly ккому: string;
  /** Что сказать по нажатию без права, когда общей фразы «его выполняет …» мало. */
  readonly безПрава?: string;
  /** Шаг ставит отметки о выплате: рядом спрашивается день перевода. */
  readonly сДнёмПеревода: boolean;
  readonly действие: () => void;
  readonly идёт: boolean;
}

export function ApproveBar({
  payroll,
  employees,
}: {
  payroll: PayrollOut;
  /** Люди той же сетки: по ним видно, скольким выплата ещё не отмечена. */
  employees: readonly PayrollEmployeeOut[] | undefined;
}) {
  const роли = useAuthStore((состояние) => состояние.permissions);
  // Отказ сервера и свой отказ полосы — две разные вещи и два разных вида. Отказ сервера
  // приходит конвертом, и общая плашка его разбирает. Свой отказ («нет права», «день перевода
  // не набран») до 19.10.2026 уходил туда же обычной ошибкой, а обычную ошибку плашка честно
  // показывает как «Сбой в программе… Обновите страницу»: человек без права читал, что
  // программа сломалась, а не к кому идти (правило 10).
  const [ошибка, поставитьОшибку] = useState<unknown>(null);
  const [запрет, поставитьЗапрет] = useState<string | null>(null);
  const [возврат, показатьВозврат] = useState(false);
  const [основание, поставитьОснование] = useState('');
  // День перевода для отметки выплаты. Деньги уходят и раньше, чем их отмечают: 11.10.2026
  // заказчик перевёл два объекта вне программы и отмечал позже.
  const [деньПеревода, поставитьДеньПеревода] = useState(todayIso);
  const [ошибкаДня, поставитьОшибкуДня] = useState<string | undefined>(undefined);

  const отправка = useSubmitPayroll(payroll.id);
  const согласование = useSecondApprovePayroll(payroll.id);
  const утверждение = useApprovePayroll(payroll.id);
  const выплата = useMarkPayrollPaid(payroll.id);
  const вернуть = useReturnPayrollToWork(payroll.id);
  const пачка = useMarkPaidBulk();

  // Правки ещё едут на сервер: числа на нём пока не те, которые человек видит.
  const записывается = useIsMutating({ mutationKey: payrollKeys.все }) > 0;

  const запустить = <Т,>(
    мутация: { mutate: (тело: Т, опции: { onError: (ошибка: unknown) => void }) => void },
    тело: Т,
  ) => {
    поставитьОшибку(null);
    поставитьЗапрет(null);
    мутация.mutate(тело, { onError: поставитьОшибку });
  };

  const шаг = выбратьШаг(
    payroll,
    {
      отправка: () => запустить(отправка, { version: payroll.version }),
      согласование: () => запустить(согласование, { version: payroll.version }),
      утверждение: () =>
        запустить(утверждение, { expected_total_payout: payroll.totals.total_payout }),
      выплата: () => запустить(выплата, { version: payroll.version, paid_on: деньПеревода }),
      пачка: () => запустить(пачка, { payroll_id: payroll.id, paid_on: деньПеревода }),
      идёт: {
        отправка: отправка.isPending,
        согласование: согласование.isPending,
        утверждение: утверждение.isPending,
        выплата: выплата.isPending,
        пачка: пачка.isPending,
      },
    },
    безОтметкиВыплаты(employees ?? []),
    суммаБезОтметкиВыплаты(employees ?? []),
  );

  const можно = шаг !== null && can(роли, шаг.право);

  return (
    // Группа с именем: по ней полосу находят и сквозные проверки, и чтение с экрана. Состояние
    // ведомости словами («На согласовании», «Утверждена») стоит и в строках реестра, и без
    // привязки к полосе такое слово на экране не одно.
    <div
      role='group'
      aria-label='Действия с ведомостью'
      className='flex flex-col gap-2 border-b border-stroke-soft-200 px-5 py-2'
    >
      {пачка.data !== undefined && (
        <p className='text-paragraph-sm text-text-sub-600'>
          {пачка.data.created === 0
            ? // Ноль отмеченных — это не «сделано»: либо всем уже отметили, либо платить
              // нечего. Молчание тут читается как «сработало».
              'Отмечать было нечего: у всех строк выплата уже отмечена либо к выплате ноль.'
            : `Отмечено выплаченными ${countLabel(пачка.data.created, ЛЮДИ)} на ${formatKopecks(
                пачка.data.total_amount,
              )}. Кому перевели не всё — поправьте построчно, в панели человека.`}
        </p>
      )}

      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex flex-col'>
          <span className='text-label-sm text-text-strong-950'>{состояниеСловами(payroll)}</span>
          {шаг !== null && (
            <span className='text-paragraph-xs text-text-sub-600'>
              {можно ? шаг.пояснение : `Это делает ${шаг.ккому}.`}
            </span>
          )}
        </div>

        <div className='flex items-center gap-2'>
          {/* Выгрузки стоят ЗДЕСЬ, в полосе действий ведомости, а не в шапке раздела: шапка
              принадлежит периоду и реестру, а файл собирается по ОТКРЫТОЙ ведомости. Их две,
              и названы они по-разному не для красоты: «в Excel» — то же, что на экране, «под
              подпись» — печатная форма с колонками «Дата» и «Подпись», альбомным листом и
              шапкой реквизитов. Именно её печатают и подписывают у людей (сценарий 40).
              До 26.08.2026 к этой двери не было клиентской функции вовсе: главный документ
              продукта нельзя было ни выгрузить, ни напечатать. */}
          <ExportButton собрать={() => exportPayroll(payroll.id, 'sheet')} />
          <ExportButton что='под подпись' собрать={() => exportPayroll(payroll.id, 'signing')} />
          {/* День перевода — только у шага, который ставит отметки о выплате (тз, раздел
              5.4.6: «вручную по строке или массовым действием по ведомости»). До утверждения
              числа ещё меняются, а у выплаченной, где отмечены все, отмечать нечего: поле без
              кнопки спрашивало бы дату ни для чего. */}
          {шаг?.сДнёмПеревода === true && (
            <DateField
              id='payroll-paid-on'
              label='Когда перевели'
              value={деньПеревода}
              onChange={(значение) => {
                поставитьДеньПеревода(значение);
                поставитьОшибкуДня(undefined);
              }}
              error={ошибкаДня}
            />
          )}
          {шаг !== null && (
            <Button.Root
              variant='primary'
              size='small'
              onClick={() => {
                поставитьОшибку(null);
                if (!можно) {
                  поставитьЗапрет(
                    шаг.безПрава ?? `Нет права на это действие: его выполняет ${шаг.ккому}.`,
                  );
                  return;
                }
                // Недонабранная дата наружу уходит пустой, а пустой день сервер читает как
                // «сегодня»: «11.10.20» молча стало бы сегодняшним днём в отметках у всех
                // людей ведомости. Панель человека останавливает такое же нажатие так же.
                if (шаг.сДнёмПеревода && деньПеревода === '') {
                  поставитьЗапрет(null);
                  поставитьОшибкуДня('Поставьте день перевода: дд.мм.гггг.');
                  return;
                }
                if (записывается) {
                  поставитьЗапрет(
                    'Одна правка ещё не записана. Подождите пару секунд и повторите.',
                  );
                  return;
                }
                шаг.действие();
              }}
            >
              {шаг.идёт ? 'Записываем…' : шаг.подпись}
            </Button.Root>
          )}
          {payroll.status === 'approved' && can(роли, ПРАВО_ВЕРНУТЬ) && (
            <Button.Root
              variant='neutral'
              mode='stroke'
              size='small'
              onClick={() => показатьВозврат((было) => !было)}
            >
              Вернуть в работу
            </Button.Root>
          )}
        </div>
      </div>

      {возврат && (
        // Единственная модальная поверхность цепочки, и она нужна: возврат отменяет погашения
        // долгов и перенос аванса, то есть деньги живых людей. Причина остаётся в журнале.
        <div className='flex flex-col gap-2 rounded-lg border border-stroke-soft-200 p-3'>
          <span className='text-label-sm text-text-strong-950'>Что именно оказалось неверно?</span>
          <span className='text-paragraph-xs text-text-sub-600'>
            Возврат отменит погашения долгов и перенос неудержанного аванса. Причина останется в
            журнале.
          </span>
          <TextField
            id='payroll-return-basis'
            label='Причина'
            value={основание}
            onChange={поставитьОснование}
            placeholder='Например: часы пришли из табеля с опозданием'
          />
          <div className='flex items-center gap-2'>
            <Button.Root
              variant='error'
              size='small'
              onClick={() => {
                поставитьОшибку(null);
                if (основание.trim().length < ОСНОВАНИЕ_МИНИМУМ) {
                  поставитьЗапрет('Напишите причину словами: не меньше десяти знаков.');
                  return;
                }
                поставитьЗапрет(null);
                вернуть.mutate(
                  { basis: основание.trim() },
                  {
                    onSuccess: () => {
                      показатьВозврат(false);
                      поставитьОснование('');
                    },
                    onError: поставитьОшибку,
                  },
                );
              }}
            >
              {вернуть.isPending ? 'Возвращаем…' : 'Вернуть в работу'}
            </Button.Root>
            <Button.Root
              variant='neutral'
              mode='ghost'
              size='small'
              onClick={() => показатьВозврат(false)}
            >
              Отмена
            </Button.Root>
          </div>
        </div>
      )}

      {запрет !== null && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {запрет}
        </Hint.Root>
      )}
      {ошибка !== null && <ErrorAlert error={ошибка} />}
    </div>
  );
}

/** Что написано слева: где ведомость сейчас и кто это сделал. */
function состояниеСловами(payroll: PayrollOut): string {
  if (payroll.status === 'paid') {
    return `Выплачена${payroll.number ? `, № ${payroll.number}` : ''}`;
  }
  if (payroll.status === 'approved') {
    return `Утверждена${payroll.number ? `, № ${payroll.number}` : ''}`;
  }
  if (payroll.status === 'review') {
    return payroll.second_approved_at !== null && payroll.second_approved_at !== undefined
      ? 'Согласована, ждёт утверждения'
      : 'На согласовании';
  }
  return 'Черновик';
}

interface Обработчики {
  readonly отправка: () => void;
  readonly согласование: () => void;
  readonly утверждение: () => void;
  /** Отметки всем без отметки и состояние «выплачена». */
  readonly выплата: () => void;
  /** Те же отметки пачкой, без смены состояния: у выплаченной, где отмечены не все. */
  readonly пачка: () => void;
  readonly идёт: {
    readonly отправка: boolean;
    readonly согласование: boolean;
    readonly утверждение: boolean;
    readonly выплата: boolean;
    readonly пачка: boolean;
  };
}

/**
 * Сколько людей ведомости ещё без отметки о выплате: итог больше нуля, действующей отметки нет.
 *
 * Отбор тот же, что у сервера при отметке пачкой (`unpaid_of_payroll`): число на кнопке обязано
 * сойтись с тем, сколько отметок встанет. Ноль к выплате не считается: отметить выплату на ноль
 * значит соврать про деньги, которых не переводили. «Действующей отметки нет» читается по пустому
 * дню выплаты: сервер кладёт туда день последней ДЕЙСТВУЮЩЕЙ отметки, а у отметки день
 * обязателен, значит пусто ровно тогда, когда действующих нет. По тому же признаку панель
 * человека решает, показывать ли форму отметки.
 */
export function безОтметкиВыплаты(люди: readonly PayrollEmployeeOut[]): number {
  return люди.filter((человек) => человек.total_payout > 0 && человек.paid_at === null).length;
}

/**
 * Сколько запишет «Отметить выплату», когда часть людей уже отмечена построчно: итог к выплате
 * тех, кого считает `безОтметкиВыплаты`, — тот же признак, та же сетка. `null` — не отмечен
 * никто: тогда кнопка называет итог ведомости с сервера, как и раньше.
 */
export function суммаБезОтметкиВыплаты(люди: readonly PayrollEmployeeOut[]): number | null {
  if (!люди.some((человек) => человек.paid_at !== null)) {
    return null;
  }
  return люди
    .filter((человек) => человек.total_payout > 0 && человек.paid_at === null)
    .reduce((итог, человек) => итог + человек.total_payout, 0);
}

/**
 * Единственное действие, которое сейчас имеет смысл. `null` — делать больше нечего.
 *
 * `безОтметки` — сколько людей без отметки о выплате (`безОтметкиВыплаты`). Важно только
 * выплаченной: у неё это решает, есть ли кнопка вообще. `суммаКОтметке` — что запишет отметка у
 * утверждённой, когда часть людей уже отмечена (`суммаБезОтметкиВыплаты`); `null` — не отмечен
 * никто, и на кнопке весь итог ведомости.
 *
 * Экспортируется ради проверки: правило «одна кнопка на состояние» проверяется таблицей, а не
 * прокликиванием четырёх экранов.
 */
export function выбратьШаг(
  payroll: PayrollOut,
  обработчики: Обработчики,
  безОтметки: number,
  суммаКОтметке: number | null = null,
): Шаг | null {
  const итог = formatKopecks(payroll.totals.total_payout);
  const людей = payroll.totals.employees_count;

  // Пока шаг едет на сервер, второе нажатие не делает НИЧЕГО.
  //
  // Утверждение на 281 строке пересчитывает всю ведомость и проводит погашения — это секунды,
  // и нажать ещё раз человек за это время успевает. Это нормальная реакция на молчание, а не
  // ошибка. Второй запрос сервер отбивает красным «ведомость утверждена: утверждение сейчас
  // нельзя», и человек видит эту надпись СРАЗУ ПОСЛЕ успешного утверждения — то есть перестаёт
  // понимать, получилось у него или нет.
  //
  // Кнопка при этом не гаснет и не исчезает: на ней написано «Записываем…», и это и есть ответ
  // на вопрос «почему ничего не произошло». Серая кнопка такого ответа не даёт.
  const пока = (идёт: boolean, действие: () => void) => (идёт ? () => {} : действие);

  if (payroll.status === 'draft') {
    return {
      подпись: 'Отправить на согласование',
      пояснение: 'Пересчитаем и проверим перед отправкой.',
      право: ПРАВО_ПОДГОТОВИТЬ,
      ккому: 'финансовый отдел',
      сДнёмПеревода: false,
      действие: пока(обработчики.идёт.отправка, обработчики.отправка),
      идёт: обработчики.идёт.отправка,
    };
  }
  if (payroll.status === 'review') {
    const согласована =
      payroll.second_approved_at !== null && payroll.second_approved_at !== undefined;
    if (!согласована) {
      return {
        подпись: 'Согласовать',
        пояснение: 'Второй уровень: премии и изменения тарифов.',
        право: ПРАВО_СОГЛАСОВАТЬ,
        ккому: 'операционное руководство',
        сДнёмПеревода: false,
        действие: пока(обработчики.идёт.согласование, обработчики.согласование),
        идёт: обработчики.идёт.согласование,
      };
    }
    return {
      подпись: `Утвердить: ${людей} чел., ${итог}`,
      пояснение: 'Суммы зафиксируются, долги спишутся на удержанное.',
      право: ПРАВО_УТВЕРДИТЬ,
      ккому: 'руководство',
      сДнёмПеревода: false,
      действие: пока(обработчики.идёт.утверждение, обработчики.утверждение),
      идёт: обработчики.идёт.утверждение,
    };
  }
  if (payroll.status === 'approved') {
    // Отмечены все, кому есть что платить: новых отметок не будет, кнопка только переводит
    // ведомость в «выплачена». «Отметить выплату: 0» читалось бы как поломка.
    if (суммаКОтметке !== null && безОтметки === 0) {
      return {
        подпись: 'Отметить ведомость выплаченной',
        пояснение:
          'Выплата отмечена у всех: новых отметок не будет, ведомость перейдёт в «выплачена».',
        право: ПРАВО_ВЫПЛАТА,
        ккому: 'финансовый отдел',
        сДнёмПеревода: false,
        действие: пока(обработчики.идёт.выплата, обработчики.выплата),
        идёт: обработчики.идёт.выплата,
      };
    }
    return {
      подпись: `Отметить выплату: ${суммаКОтметке === null ? итог : formatKopecks(суммаКОтметке)}`,
      пояснение:
        суммаКОтметке === null
          ? `Кому выплата ещё не отмечена, встанет отметка на весь итог днём перевода. ${СНАЧАЛА_ЧАСТИЧНЫЕ}`
          : `Отметки, уже поставленные построчно, не тронем. Без отметки ${countLabel(безОтметки, ЛЮДИ)}: им встанет отметка на весь итог днём перевода. ${СНАЧАЛА_ЧАСТИЧНЫЕ}`,
      право: ПРАВО_ВЫПЛАТА,
      ккому: 'финансовый отдел',
      сДнёмПеревода: true,
      действие: пока(обработчики.идёт.выплата, обработчики.выплата),
      идёт: обработчики.идёт.выплата,
    };
  }
  // Число в подписи: человек жмёт, зная, сколько отметок встанет. Оно же — проверка: если в
  // итоге пачки окажется другое, кто-то успел отметить или сторнировать, пока он смотрел.
  if (payroll.status === 'paid' && безОтметки > 0) {
    return {
      подпись: `Отметить выплаченными остальных (${безОтметки})`,
      пояснение: `Отметки о выплате есть не у всех: остальным встанет отметка на весь итог днём перевода. ${СНАЧАЛА_ЧАСТИЧНЫЕ}`,
      право: ПРАВО_ОТМЕТИТЬ,
      ккому: 'финансовый отдел или администратор',
      безПрава:
        'Отмечает выплату финансовый отдел или администратор. Скопируйте ссылку на эту ведомость и передайте им.',
      сДнёмПеревода: true,
      действие: пока(обработчики.идёт.пачка, обработчики.пачка),
      идёт: обработчики.идёт.пачка,
    };
  }
  return null;
}
